<script lang="ts">
  import { goto } from '$app/navigation';
  import type {
    BulkClassStudentUpdateAction,
    ClinicalGroupSummary,
    Course,
    Leaderboard,
    SortOrder,
  } from '$lib/api/types';
  import SortButton from '../common/SortButton.svelte';
  import { page } from '$app/state';
  import InfiniteScroll from '../common/InfiniteScroll.svelte';
  import { listStudents } from '$lib/api/classes';
  import TableError from '../TableError.svelte';
  import TableMessage from '../TableMessage.svelte';
  import CleanDropdown from '../common/CleanDropdown.svelte';
  import CheckIconDropdownItem from '../common/CheckIconDropdownItem.svelte';
  import StudentDetailsModal from './StudentDetailsModal.svelte';
  import InlineBadgeProgress from './InlineBadgeProgress.svelte';
  import DropdownItem from '../common/bootstrap/DropdownItem.svelte';
  import AddStudentToClassFormModal from './AddStudentToClassFormModal.svelte';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faCheck } from '@itdev-fortawesome/pro-solid-svg-icons/faCheck';
  import { faXmark } from '@itdev-fortawesome/pro-solid-svg-icons';
  import ConfirmBulkAction from './ConfirmBulkAction.svelte';
  import { SvelteSet } from 'svelte/reactivity';
  import BulkImportModals from './BulkImportModals.svelte';
  import { auth } from '$lib/stores/auth.svelte';
  import ConfirmLeaderboardReset from './ConfirmLeaderboardReset.svelte';

  interface Props {
    classId: number;
    course: Course;
    clinicalGroups: ClinicalGroupSummary[];
  }

  let { classId, course, clinicalGroups }: Props = $props();
  let pathname = $derived(page.url.pathname);
  let currentSort = $state(page.url.searchParams.get('sortby') ?? 'score');
  let currentSortOrder: 'asc' | 'desc' = $state((page.url.searchParams.get('order') as SortOrder | null) ?? 'desc');

  let initialClinicalGroupId: number | null = null;
  if (page.url.searchParams.has('clinicalGroup') && page.url.searchParams.get('clinicalGroup')!.length > 0) {
    initialClinicalGroupId = parseInt(page.url.searchParams.get('clinicalGroup')!);
  }

  let clinicalGroupIdFilter: number | null = $state(initialClinicalGroupId);
  let clinicalGroupFilterDropdownTitle = $derived.by(() => {
    if (clinicalGroupIdFilter === null)
      return `All students (${clinicalGroups.length} clinical group${clinicalGroups.length === 1 ? '' : 's'})`;
    if (clinicalGroupIdFilter === -1) return 'No Clinical Group';
    return clinicalGroups.find(x => x.id === clinicalGroupIdFilter)!.name;
  });

  let queryString = $derived.by(() => {
    const searchParams = page.url.searchParams.toString();
    if (!searchParams || searchParams.length === 0) return 'clinicalGroup=&sortby=score&order=desc';
    return page.url.searchParams.toString();
  });

  let studentDetailsSelectedStudent: Leaderboard | null = $state(null);
  let isAddingNewStudent = $state(false);
  let selectedStudents = new SvelteSet<number>();
  let confirmBulkAction: BulkClassStudentUpdateAction | null = $state(null);
  let isBulkImporting = $state(false);
  let isResettingLeaderboard = $state(false);

  $effect(() => {
    const clinicalGroupId = clinicalGroupIdFilter?.toString() ?? '';
    goto(`${pathname}?clinicalGroup=${clinicalGroupId}&sortby=${currentSort}&order=${currentSortOrder}`, {
      replaceState: true,
      keepFocus: true,
      noScroll: true,
    });
  });
</script>

<div class="d-flex gap-3 align-items-center">
  <CleanDropdown title={clinicalGroupFilterDropdownTitle} dropdownTglClass="btn-sm">
    <CheckIconDropdownItem active={clinicalGroupIdFilter === null} onclick={() => (clinicalGroupIdFilter = null)}>
      All Students ({clinicalGroups.length} clinical group{clinicalGroups.length === 1 ? '' : 's'})
    </CheckIconDropdownItem>
    {#if auth.isAdmin}
      <CheckIconDropdownItem active={clinicalGroupIdFilter === -1} onclick={() => (clinicalGroupIdFilter = -1)}>
        No Clinical Group
      </CheckIconDropdownItem>
    {/if}
    <DropdownItem divider />
    {#each clinicalGroups as grp (grp.id)}
      <CheckIconDropdownItem active={clinicalGroupIdFilter === grp.id} onclick={() => (clinicalGroupIdFilter = grp.id)}>
        {grp.name}
      </CheckIconDropdownItem>
    {/each}
  </CleanDropdown>
  <div class="flex-grow-1"></div>
  {#if auth.isAdmin}
    <CleanDropdown
      title="{selectedStudents.size} student{selectedStudents.size === 1 ? '' : 's'} selected"
      disabled={selectedStudents.size === 0}
      dropdownTglClass="btn-sm d-none d-md-block"
    >
      <DropdownItem onclick={async () => (confirmBulkAction = 'AssignClinicalGroup')}
        >Assign Clinical Group</DropdownItem
      >
      <DropdownItem onclick={async () => (confirmBulkAction = 'Remove')}>Remove from Class</DropdownItem>
    </CleanDropdown>
    <button class="btn btn-primary btn-sm d-none d-md-block" onclick={() => (isBulkImporting = true)}>
      Bulk Import
    </button>
    <button class="btn btn-primary btn-sm" onclick={() => (isAddingNewStudent = true)}> Add Student </button>
  {/if}
</div>

<div class="overflow-scroll">
  <InfiniteScroll
    loadData={async (query, page, pageSize, signal) => {
      return await listStudents(classId, query, page, pageSize, signal);
    }}
    {queryString}
    minimumQueryStringLength={0}
  >
    {#snippet dataRenderer(students, query, shouldShowLoadingIndicator, error, loadingIndicator)}
      <table class="table table-sm table-borderless table-hover mt-3">
        <thead>
          <tr class="border-bottom">
            {#if auth.isAdmin}
              <th scope="col" class="mdvp-hidden" style="width: 1em;"></th>
            {/if}
            <th scope="col">
              <div class="name-column">
                Last Name
                <SortButton
                  sortColumnLabel="last name"
                  bind:currentSortOrder
                  bind:currentSort
                  sortColumn="lastName"
                  small
                />
              </div>
            </th>
            <th scope="col">
              <div class="name-column">
                First Name
                <SortButton
                  sortColumnLabel="first name"
                  bind:currentSortOrder
                  bind:currentSort
                  sortColumn="firstName"
                  small
                />
              </div>
            </th>
            <th scope="col" class="nowrap mdvp-hidden">
              Clinical Group
              <SortButton
                sortColumnLabel="clinical group"
                bind:currentSortOrder
                bind:currentSort
                sortColumn="clinicalGroup"
                small
              />
            </th>
            <th scope="col" class="smvp-hidden"><div class="no-sort-icon">Badge Progress</div></th>
            <th scope="col" class="text-center mdvp-hidden"><div class="no-sort-icon">Opted In</div></th>
            <th scope="col" class="text-end nowrap">
              Score
              <SortButton
                sortColumnLabel="leaderboard score"
                bind:currentSortOrder
                bind:currentSort
                sortColumn="score"
                small
              />
            </th>
          </tr>
        </thead>
        <tbody>
          {#each students as lbItem (lbItem.studentId)}
            <tr
              onclick={e => {
                if ((e.target as HTMLElement).tagName !== 'INPUT') {
                  studentDetailsSelectedStudent = lbItem;
                }
              }}
            >
              {#if auth.isAdmin}
                <td style="width: 1em" class="mdvp-hidden">
                  <input
                    onchange={e => {
                      if (e.currentTarget.checked) {
                        selectedStudents.add(lbItem.studentId);
                      } else {
                        selectedStudents.delete(lbItem.studentId);
                      }
                    }}
                    type="checkbox"
                    class="form-check-input"
                    aria-label="include {lbItem.student.lastName}, {lbItem.student.firstName} in bulk actions"
                  />
                </td>
              {/if}
              <td><div class="name-column">{lbItem.student.lastName}</div></td>
              <td><div class="name-column">{lbItem.student.firstName}</div></td>
              <td class="mdvp-hidden">{lbItem.clinicalGroup?.name}</td>
              <td class="badge-progress-column smvp-hidden">
                {#each course.skillCategories as skillCat (`${lbItem.studentId}.${skillCat.id}`)}
                  <InlineBadgeProgress
                    {skillCat}
                    progress={lbItem.badgeProgress.find(x => x.skillCategoryId === skillCat.id)}
                  />
                {/each}
              </td>
              <td class="text-center mdvp-hidden">
                <span class="visually-hidden">
                  {#if lbItem.isOptedIn}
                    Yes
                  {:else}
                    No
                  {/if}
                </span>
                {#if lbItem.isOptedIn}
                  <FontAwesomeIcon icon={faCheck} />
                {:else}
                  <FontAwesomeIcon icon={faXmark} class="text-tertiary" />
                {/if}
              </td>
              <td class="text-end">{lbItem.score}</td>
            </tr>
          {/each}
        </tbody>
      </table>
      {#if error}
        <TableError {error} />
      {:else if shouldShowLoadingIndicator}
        {@render loadingIndicator()}
      {:else if students.length === 0}
        <TableMessage message="No students" />
      {:else}
        <div class="d-flex justify-content-center gap-3 fst-italic align-items-center">
          <div>
            {students.length} student(s)
          </div>
          {#if auth.isAdmin}
            <button class="btn btn-link fst-italic fw-light" onclick={() => (isResettingLeaderboard = true)}
              >Reset Leaderboard</button
            >
          {/if}
        </div>
        <!-- <TableMessage message="{students.length} student(s)" /> -->
      {/if}
    {/snippet}
    {#snippet loadingIndicator()}
      <TableMessage message="Loading..." />
    {/snippet}
  </InfiniteScroll>
</div>

{#if studentDetailsSelectedStudent !== null}
  <StudentDetailsModal
    lb={studentDetailsSelectedStudent}
    onclose={() => (studentDetailsSelectedStudent = null)}
    {course}
  />
{/if}

{#if isAddingNewStudent}
  <AddStudentToClassFormModal {classId} bind:isAddingStudent={isAddingNewStudent} />
{/if}

{#if confirmBulkAction !== null}
  <ConfirmBulkAction bind:action={confirmBulkAction} {classId} students={selectedStudents} />
{/if}

{#if isBulkImporting}
  <BulkImportModals bind:isBulkImporting {classId} />
{/if}

{#if isResettingLeaderboard}
  <ConfirmLeaderboardReset bind:isResettingLeaderboard {classId} />
{/if}

<style>
  .table {
    font-size: 16px;
  }

  .table > tbody > tr > * {
    cursor: pointer;
    vertical-align: middle;
  }

  .no-sort-icon {
    /* small vertical alignment adjustment because the sort icon screws up baseline */
    margin-bottom: 2px;
  }

  .nowrap {
    white-space: nowrap;
  }

  .name-column {
    width: 150px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .badge-progress-column {
    display: flex;
    align-items: end;
    justify-content: space-around;
    gap: 0.2rem 1rem;
    white-space: nowrap;
    flex-wrap: wrap;
  }

  @media (max-width: 767px) {
    .name-column {
      width: 100px;
    }
  }
</style>
